/**
 * Dashboard card showing awaiting_approval agent decisions with action buttons.
 * Uses shared useApprovalActions hook for DRY action handling.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { historyService } from '@/services/history';
import { useApprovalActions } from '@/hooks/useApprovalActions';

export function PendingApprovalsCard() {
  const { handleApprove, handleReject, handleQueue, isProcessing } = useApprovalActions();

  const { data, isLoading } = useQuery({
    queryKey: ['agent', 'pending-approvals'],
    queryFn: () =>
      historyService.getAgentDecisions({
        outcome: 'awaiting_approval',
        pageSize: 10,
      }),
    refetchInterval: 15000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            Pending Approvals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-12 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const decisions = data?.decisions ?? [];

  if (decisions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            Pending Approvals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-2">No pending approvals</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-yellow-500/30">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          Pending Approvals ({decisions.length})
        </CardTitle>
        <Link
          to="/apps/trading-agent/approvals"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          View all
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {decisions.map((decision) => (
          <div
            key={decision.id}
            className="flex items-center gap-3 text-sm border rounded-lg p-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{decision.symbol}</span>
                <span
                  className={cn(
                    'text-xs font-medium px-1.5 py-0.5 rounded',
                    decision.decisionType === 'buy'
                      ? 'bg-green-500/10 text-green-600'
                      : 'bg-red-500/10 text-red-600'
                  )}
                >
                  {decision.decisionType.toUpperCase()}
                </span>
                <span className="text-xs text-muted-foreground">
                  {decision.quantity} @ {decision.price ? formatCurrency(decision.price) : '-'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {decision.reasoning}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDate(decision.createdAt)} &middot; {Math.round(decision.confidence * 100)}% confidence
              </p>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                title="Reject"
                onClick={() => handleReject(decision.id)}
                disabled={isProcessing(decision.id)}
              >
                <XCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                title="Queue for Market Open"
                onClick={() => handleQueue(decision.id)}
                disabled={isProcessing(decision.id)}
              >
                <Clock className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-green-500 hover:text-green-600 hover:bg-green-50"
                title="Approve & Execute"
                onClick={() => handleApprove(decision.id)}
                disabled={isProcessing(decision.id)}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
